import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  AttachmentFolderInstanceService,
  BuildingContactMergeDto,
  BuildingEmergencyContactDto,
  BuildingEmergencyContactMergeDto,
  BuildingHouseCareContactDto,
  BuildingHouseCareContactMergeDto,
  BuildingMergeDto,
  BuildingSectionService,
  BuildingService,
  TenantItemDto,
  TenantMergeDto,
  TenantService,
} from '@artemis-software/wr-api';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { SubFolderDialogComponent } from '@components/dialog/sub-folder-dialog/sub-folder-dialog.component';
import { PendingChanges } from '@guards/pending-changes.guard';
import { BuildingScanListComponent } from '@components/building-scan-list/building-scan-list.component';
import {
  appendSubFolderToValue,
  buildingDocumentsDtoToValue,
  buildingDocumentsValueToMergeDto,
  folderInstanceDtosToFolderInstances,
  removeSubFolderFromValue,
  renameSubFolderInValue,
} from '@shared/form-controls/attachment-folder-tree/attachment-folder-utils';
import { CurrentUserService } from '@services/current-user.service';
import { currentTenant } from '@/utils/admin-utils';
import { canViewRepairTasks } from '@/utils/repair-task-visibility';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { queryParam, routeParam } from '@/utils/signals/router';
import { loader } from '@/utils/signals/loader';
import { createForm } from '@sonofabit/ng-core';
import {
  AttachmentFolderInstance,
  BuildingDocumentsValue
} from '@shared/form-controls/attachment-folder-tree/attachment-folder-tree.component';
import {
  BuildingTab,
  buildingTabConfig,
  buildingTabs,
  buildingTabTranslations
} from '@pages/building-detail-page/building-tabs';
import { BuildingSectionEditorComponent } from '@components/building-section-editor/building-section-editor.component';
import {
  BuildingBaseDataFormComponent
} from '@pages/building-detail-page/building-form-page/building-base-data-form/building-base-data-form.component';
import {
  BuildingAllocationFormComponent
} from '@pages/building-detail-page/building-form-page/building-allocation-form/building-allocation-form.component';
import {
  BuildingAccessFormComponent
} from '@pages/building-detail-page/building-form-page/building-access-form/building-access-form.component';
import {
  BuildingAmenityFormComponent
} from '@pages/building-detail-page/building-form-page/building-amenity-form/building-amenity-form.component';
import {
  BuildingEnergyPassComponent
} from '@pages/building-detail-page/building-energy-pass/building-energy-pass.component';
import {
  BuildingContactsComponent
} from '@pages/building-detail-page/building-contacts/building-contacts.component';
import TypeEnum = TenantItemDto.TypeEnum;

@Component({
  standalone: false,
  selector: 'wr-building-detail-page',
  templateUrl: './building-detail-page.component.html',
  styleUrls: ['./building-detail-page.component.scss'],
})
export class BuildingDetailPageComponent implements PendingChanges {

  readonly scanList = viewChild(BuildingScanListComponent);

  private readonly router = inject(Router);
  private readonly buildingService = inject(BuildingService);
  private readonly dialogService = inject(NbDialogService);
  private readonly tenantService = inject(TenantService);
  private readonly toastrService = inject(NbToastrService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly buildingSectionService = inject(BuildingSectionService);
  private readonly attachmentFolderInstanceService = inject(AttachmentFolderInstanceService);

  protected readonly TenantTypeEnum = TenantMergeDto.TenantTypeEnum;
  protected readonly buildingTabTranslations = buildingTabTranslations;
  protected readonly BuildingTab = BuildingTab;
  isAttachmentFolderEditing = false;

  readonly buildingId = routeParam('id', true);
  readonly loader = loader();
  readonly canEditFolderContents = computed(() => this.currentUserService.hasPrivilege('editAttachmentFolderContents'));
  readonly currentTenant = currentTenant();
  readonly isEditing = signal(false);
  readonly sensorSearch = signal('');
  readonly currentTabFromUrl = queryParam('tab');
  readonly baseDataForm = viewChild(BuildingBaseDataFormComponent);
  readonly allocationForm = viewChild(BuildingAllocationFormComponent);
  readonly accessForm = viewChild(BuildingAccessFormComponent);
  readonly amenityForm = viewChild(BuildingAmenityFormComponent);
  readonly buildingSectionEditor = viewChild(BuildingSectionEditorComponent);
  readonly buildingContacts = viewChild(BuildingContactsComponent);
  readonly energyPass = viewChild(BuildingEnergyPassComponent);

  readonly building = asyncComputed(() => {
    const buildingId = this.buildingId();
    if (buildingId == 'new') {
      return undefined;
    }
    return this.buildingService.findById(buildingId);
  }, undefined, this.loader);

  readonly buildingSections = asyncComputed(() => {
    const building = this.building();
    if (!building) {
      return undefined;
    }
    return this.buildingSectionService.getGroupedBuildingSectionsByBuildingId(building.id);
  }, undefined, this.loader);

  readonly buildingSectionCount = computed(() => {
    const sections = this.buildingSections();
    if (!sections) {
      return null;
    }
    return sections.reduce((count, group) => count + group.houseNumbers.length, 0);
  });

  readonly currentExternalTenantId = computed(() => {
    const building = this.building();
    return building?.allocationData.ownerTenant.id;
  });

  readonly canEdit = computed(() => {
    const building = this.building();
    if (!building) {
      return true;
    }
    return building.permissions.canEdit;
  });

  readonly currentTab = computed(() => {
    const tab = this.currentTabFromUrl() as BuildingTab;
    return Object.values(BuildingTab).includes(tab) ? tab : BuildingTab.BuildingData;
  });

  readonly contractorScopes = asyncComputed(() => {
    const selectedExternalTenantId = this.currentExternalTenantId();
    const currentTenant = this.currentTenant();
    if (selectedExternalTenantId && currentTenant && currentTenant.type === TypeEnum.Contractor) {
      return this.tenantService.findContractorScope(currentTenant.id, selectedExternalTenantId);
    }
    return undefined;
  }, undefined, this.loader);

  readonly activeTabs = computed(() => {
    return buildingTabs.filter(tab => this.isTabVisible(tab));
  });

  readonly form = computed(() => {
    const building = this.building();
    const sections = this.buildingSections();

    return createForm(({ field }) => {
      return {
        id: field({ value: building?.id }),
        internalData: field({ value: building?.internalNumber ?? null }),
        baseData: field({ value: building?.baseData ?? null }),
        allocationData: field({ value: building?.allocationData ?? null }),
        accessData: field({ value: building?.accessData ?? null }),
        amenityData: field({ value: building?.amenityData ?? null }),

        buildingSections: field({ value: sections ?? [] }),
        contractorAssignments: field({ value: building?.contractorAssignments ?? [] }),
        billingAddresses: field({ value: building?.billingAddresses ?? [] }),
        contacts: field({ value: building?.contacts ?? [] }),

        attachmentFolderInstances: field({
          value: building
            ? buildingDocumentsDtoToValue(building)
            : { folderInstances: [] } as BuildingDocumentsValue,
        }),
        assignedInternalTenants: new FormControl(building?.assignedInternalTenants),
        note: new FormControl(building?.note),
        sendMaintenanceNotifications: new FormControl(building?.sendMaintenanceNotifications),
        ...(() => {
          const TypeEnum = BuildingEmergencyContactDto.TypeEnum;
          const byType = Object.fromEntries((building?.emergencyContacts ?? []).map(c => [c.type, c.contractor.id]));
          return {
            waterDamageContractorId: new FormControl(byType[TypeEnum.WaterDamage] ?? null),
            heatingFailureContractorId: new FormControl(byType[TypeEnum.HeatingFailure] ?? null),
            stormDamageContractorId: new FormControl(byType[TypeEnum.StormDamage] ?? null),
          };
        })(),
        ...(() => {
          const TypeEnum = BuildingHouseCareContactDto.TypeEnum;
          const byType = Object.fromEntries((building?.houseCareContacts ?? []).map(c => [c.type, c.contractor.id]));
          return {
            cleaningContractorId: new FormControl(byType[TypeEnum.Cleaning] ?? null),
            greenAreaMaintenanceContractorId: new FormControl(byType[TypeEnum.GreenAreaMaintenance] ?? null),
            winterServiceContractorId: new FormControl(byType[TypeEnum.WinterService] ?? null),
          };
        })(),
      };
    });
  });

  readonly isSaveButtonVisible = computed(() => {
    const tab = this.currentTab();
    if (tab === BuildingTab.EnergyCertificate) {
      return false;
    }
    if (tab === BuildingTab.HoraPass) {
      return false;
    }
    if (tab === BuildingTab.Documents) {
      return this.canEditFolderContents();
    }
    return this.canEdit();
  });

  constructor() {
    if (this.buildingId() === 'new') {
      this.isEditing.set(true);
    }

    effect(() => {
      const form = this.form();
      const editing = this.isEditing();
      const canEdit = this.canEdit();

      if (canEdit && editing) {
        form.enable();
      } else {
        form.disable();
      }
    });
  }

  private isTabVisible(tab: BuildingTab): boolean {
    const config = buildingTabConfig[tab];
    const isNew = this.buildingId() === 'new';
    const currentTenant = this.currentTenant();

    if (isNew && !config.visibleWhenNew) {
      return false;
    }

    if ((tab === BuildingTab.ProfiNeo || tab === BuildingTab.ProfiNeoSanierung || tab === BuildingTab.SubTenant || tab === BuildingTab.Notes || tab === BuildingTab.HoraPass) && currentTenant?.type !== 'Internal') {
      return false;
    }

    if (tab === BuildingTab.Sensors && !this.currentUserService.hasPrivilege('viewSensorDashboards')) {
      return false;
    }

    if (tab === BuildingTab.EnergyCertificate && !this.currentUserService.hasPrivilege('editEnergyCertificates')) {
      return false;
    }

    if (tab === BuildingTab.BuildingEquipments && !this.currentUserService.canViewTgas()) {
      return false;
    }

    const user = this.currentUserService.currentUser();
    if (tab === BuildingTab.RepairTasks && user !== undefined && !canViewRepairTasks(user)) {
      return false;
    }

    if (config.visibilityScope) {
      switch (currentTenant?.type) {
        case 'External':
        case 'Internal': {
          return true;
        }

        case 'Contractor': {
          const contractorScopes = this.contractorScopes();
          if (!contractorScopes) return false;

          const requiredScope = config.visibilityScope.scope;
          if (requiredScope && !contractorScopes.scopes.includes(requiredScope)) {
            return false;
          }

          const requiredPermission = config.visibilityScope.permission;
          return !(requiredPermission && !this.currentUserService.hasPrivilege(requiredPermission));
        }
      }
    }
    return true;
  }

  tabChange(event: { tabId: string }): void {
    this.router.navigate([], {
      queryParams: { tab: event.tabId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  enableAttachmentFolders(): void {
    this.isAttachmentFolderEditing = true;
    this.form().get('attachmentFolderInstances')?.enable();
  }

  private patchDocumentsValue(mutate: (value: BuildingDocumentsValue) => BuildingDocumentsValue): void {
    const control = this.form().get('attachmentFolderInstances')!;
    control.setValue(mutate(control.value as BuildingDocumentsValue));
  }

  async openCreateSubFolderDialog(parent: AttachmentFolderInstance): Promise<void> {
    const result = await firstValueFrom(this.dialogService.open(SubFolderDialogComponent, {
      context: { header: 'Unterordner anlegen' },
    }).onClose);
    if (!result) {
      return;
    }
    try {
      const created = await firstValueFrom(this.attachmentFolderInstanceService.merge({
        parentId: parent.id,
        name: result.name,
        color: result.color,
      }));
      const createdInstance = folderInstanceDtosToFolderInstances([created])[0];
      this.patchDocumentsValue((value) => appendSubFolderToValue(value, parent.id!, createdInstance));
      this.toastrService.success('Unterordner wurde erfolgreich angelegt', 'Ordner');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnte nicht angelegt werden.', 'Fehler');
    }
  }

  async openRenameSubFolderDialog(subFolder: AttachmentFolderInstance): Promise<void> {
    const result = await firstValueFrom(this.dialogService.open(SubFolderDialogComponent, {
      context: { header: 'Unterordner bearbeiten', name: subFolder.name, color: subFolder.color },
    }).onClose);
    if (!result) {
      return;
    }
    try {
      await firstValueFrom(this.attachmentFolderInstanceService.merge({
        id: subFolder.id,
        name: result.name,
        color: result.color,
      }));
      this.patchDocumentsValue((value) => renameSubFolderInValue(value, subFolder, result.name, result.color));
      this.toastrService.success('Unterordner wurde erfolgreich gespeichert', 'Ordner');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnte nicht gespeichert werden.', 'Fehler');
    }
  }

  confirmDeleteSubFolder(subFolder: AttachmentFolderInstance): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Unterordner löschen?',
        message: `Der Unterordner "${subFolder.name}" und alle enthaltenen Dokumente werden gelöscht.`,
      },
    }).onClose.subscribe({
      next: async (result) => {
        if (!result) {
          return;
        }
        try {
          await firstValueFrom(this.attachmentFolderInstanceService.deleteById(subFolder.id!));
          this.patchDocumentsValue((value) => removeSubFolderFromValue(value, subFolder));
          this.toastrService.success('Unterordner wurde erfolgreich gelöscht', 'Ordner');
        } catch (e) {
          console.error(e);
          this.toastrService.danger('Unterordner konnte nicht gelöscht werden.', 'Fehler');
        }
      },
    });
  }

  onEdit(): void {
    this.isEditing.set(true);
  }

  async submitEnergyPass(): Promise<void> {
    const energyPass = this.energyPass();
    if (!energyPass) {
      return;
    }
    const success = await energyPass.save();
    if (success) {
      this.isEditing.set(false);
    }
  }

  cancelEnergyPass(): void {
    this.energyPass()?.cancel();
    this.isEditing.set(false);
  }

  async cancel(): Promise<void> {
    if (this.buildingId() === 'new') {
      await this.router.navigate(['buildings']);
    } else {
      this.isEditing.set(false);
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['building', this.buildingId()]);
      });
    }
  }

  async submitBuildingData(): Promise<void> {
    const formGroup = this.form();
    this.markAllFormsAsTouched();

    if (formGroup.invalid) {
      this.toastrService.danger('Bitte füllen Sie alle erforderlichen Felder aus.', 'Fehler');
      return;
    }

    try {
      const buildingMergeDto = this.mapFormToMergeDto(formGroup.value);
      if (!buildingMergeDto.buildingSections?.length) {
        this.toastrService.danger('Gebäude muss mindestens einen Gebäudeteil haben.', 'Gebäude');
        return;
      }
      const updatedBuilding = await firstValueFrom(
        this.buildingService.merge(buildingMergeDto)
      );

      this.toastrService.success('Gebäude erfolgreich gespeichert', 'Erfolg');
      this.isEditing.set(false);

      const targetId = updatedBuilding.id;
      const targetTab = this.currentTab();
      await this.router.navigateByUrl('/', { skipLocationChange: true });
      await this.router.navigate(['building', targetId], {
        queryParams: { tab: targetTab },
        replaceUrl: true
      });

    } catch (e) {
      console.error('Save failed:', e);
      this.toastrService.danger('Speichern fehlgeschlagen.', 'Fehler');
    }
  }

  private markAllFormsAsTouched() {
    this.form().markAllAsTouched();
    this.baseDataForm()?.markAsTouched();
    this.allocationForm()?.markAsTouched();
    this.accessForm()?.markAsTouched();
    this.amenityForm()?.markAsTouched();
    this.buildingSectionEditor()?.markAsTouched();
    this.buildingContacts()?.markAsTouched();
  }

  async onDelete(): Promise<void> {
    const building = this.building();

    if (!building) {
      return;
    }

    const result = await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Gebäude löschen',
        message: 'Bist du sicher, dass du dieses Gebäude löschen willst?',
      },
    }).onClose);

    if (result) {
      await firstValueFrom(this.buildingService.deleteById(building.id));
      await this.router.navigate(['buildings']);
      this.toastrService.success('Gebäude wurde erfolgreich gelöscht', 'Gebäude');
    }
  }

  private mapFormToMergeDto(formValue: any): BuildingMergeDto {
    const emergencyContacts: BuildingEmergencyContactMergeDto[] = [
      { contractorId: formValue.waterDamageContractorId, type: BuildingEmergencyContactDto.TypeEnum.WaterDamage },
      { contractorId: formValue.heatingFailureContractorId, type: BuildingEmergencyContactDto.TypeEnum.HeatingFailure },
      { contractorId: formValue.stormDamageContractorId, type: BuildingEmergencyContactDto.TypeEnum.StormDamage },
    ].filter(c => c.contractorId != null) as BuildingEmergencyContactMergeDto[];

    const houseCareContacts: BuildingHouseCareContactMergeDto[] = [
      { contractorId: formValue.cleaningContractorId, type: BuildingHouseCareContactDto.TypeEnum.Cleaning },
      { contractorId: formValue.greenAreaMaintenanceContractorId, type: BuildingHouseCareContactDto.TypeEnum.GreenAreaMaintenance },
      { contractorId: formValue.winterServiceContractorId, type: BuildingHouseCareContactDto.TypeEnum.WinterService },
    ].filter(c => c.contractorId != null) as BuildingHouseCareContactMergeDto[];

    const contacts: BuildingContactMergeDto[] = (formValue.contacts ?? [])
      .filter((c: any) => !!c.name?.trim())
      .map((c: any) => ({
        id: c.id ?? undefined,
        name: c.name.trim(),
        email: c.email?.trim() || undefined,
        phone: c.phone?.trim() || undefined,
      }));

    return {
      id: this.buildingId() === 'new' ? undefined : this.buildingId(),
      baseData: formValue.baseData,
      allocationData: {
        ownerTenantId: formValue.allocationData?.ownerTenantId || formValue.allocationData?.ownerTenant?.id,
        propertyManagerId: formValue.allocationData?.propertyManager?.id,
        embeddedPropertyManager: formValue.allocationData?.embeddedPropertyManager,
        owner: formValue.allocationData?.owner,
        propertyCaretaker: formValue.allocationData?.propertyCaretaker,
        assistant: formValue.allocationData?.assistant,
      },
      accessData: formValue.accessData,
      amenityData: formValue.amenityData,
      internalNumber: formValue.internalData,
      note: formValue.note,
      sendMaintenanceNotifications: formValue.sendMaintenanceNotifications,
      buildingSections: (formValue.buildingSections ?? []).flatMap((group: any) =>
        (group.houseNumbers ?? []).map((hn: any) => ({
          id: hn.sectionId || null,
          addressId: hn.addressId || null,
          street: group.street,
          zipCode: group.automaticAddress?.zipCode,
          city: group.automaticAddress?.city,
          state: group.automaticAddress?.state,
          houseNumber: hn.number,
          markedForDeletion: !!hn.markedForDeletion,
          visible: hn.visible ?? true
        }))
      ),
      billingAddresses: (formValue.billingAddresses ?? []).map((addr: any) => ({
        ...addr,
        country: addr.country ?? 'AT',
        markedForDeletion: !!addr.markedForDeletion
      })),
      assignedInternalTenantIds: formValue.assignedInternalTenants?.map((t: any) => t.id) ?? [],
      contractorAssignments: formValue.contractorAssignments?.map((ca: any) => ({
        contractorId: ca.contractor?.id,
        facilityElement: ca.facilityElement
      })),
      emergencyContacts,
      houseCareContacts,
      contacts,
    };
  }

  async submitAttachmentFolders(): Promise<void> {
    try {
      const rawValue = this.form().getRawValue();
      const buildingId = rawValue.id;
      if (!buildingId) {
        return;
      }
      const value = rawValue.attachmentFolderInstances as BuildingDocumentsValue;

      await firstValueFrom(
        this.buildingService.mergeAttachmentFolderInstanceMerge(
          buildingDocumentsValueToMergeDto(buildingId, value),
        )
      );
      this.isAttachmentFolderEditing = false;
      this.form().get('attachmentFolderInstances')?.disable();
      this.toastrService.success('Dateien wurden erfolgreich gespeichert', 'Gebäude');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Dateien konnten nicht gespeichert werden.', 'Fehler');
    }
  }

  async navigateBack(): Promise<void> {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      await this.router.navigate(['buildings']);
    }
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing() ||
      (this.scanList()?.hasPendingChanges() ?? false) ||
      (this.energyPass()?.hasPendingChanges() ?? false);
  }

}
